import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import TagList from '../Common/TagList';
import WordCountInfo from './WordCountInfo';
import questionMarkIcon from '../../images/question_mark.svg';
import './ReplyScreen.css';
import ChatGPTInput from '../ChatGPTInput';
import InfoScreen from '../InfoScreen';
import { suggestMoralArguments, identifyLogicalFlaws, validateFrame } from '../../utils/apiHelpers';
import { logEvent } from '../../utils/Logger';
import MoralAlignment from './MoralAlignment';
import { Tag as TagType, Sentence, TagMatch } from '../../types/types';
import TagEditor from '../TagEditor/TagEditor';
import CommentContent from '../Common/CommentContent';
import HighlightToggle from '../Common/HighlightToggle';

interface ReplyScreenProps {
  tags?: string[];
  id: string;
}

interface ApiCallResult {
  suggestions?: Array<{ title: string; idea: string }>;
  logicalFlaws?: Array<{ problem: string; improvement: string }>;
  frameValidation?: {
    mainFrame?: string;
    isAligned?: boolean;
    explanation?: string;
    ideas?: string[];
  };
}

const ReplyScreen: React.FC<ReplyScreenProps> = ({ id }) => {
  const [comment, setComment] = useState('');
  const previousDraftRef = useRef<string>('');
  const previousApiResponseRef = useRef<ApiCallResult>({});
  const [showWordCountInfo, setShowWordCountInfo] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // const tags: TagType[] = location.state?.tags || [];
  const [isHighlightEnabled, setIsHighlightEnabled] = useState(false);
  const [tags, setTags] = useState<TagType[]>(location.state?.tags || []);
  const [targetTags, setTargetTags] = useState<TagType[]>([]);
  const sentences: Sentence[] = location.state?.sentences || [];
  const originalCommentTitle: string = location.state?.title || '';
  const originalComment: string = location.state?.content || '';
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastApiCallResult, setLastApiCallResult] = useState<ApiCallResult>({});
  const [newApiCallMade, setNewApiCallMade] = useState(false);
  const [frameValidationResult, setFrameValidationResult] = useState<{
    mainFrame?: string;
    isAligned?: boolean;
    explanation?: string;
    ideas?: string[];
  }>({});

  const transformedTagMatch: TagMatch[] | undefined = sentences?.map(sentence => ({
    tag: sentence.frame,
    text: sentence.text,
    explanation: sentence.explanation
  }));
  const [highlightedTag, setHighlightedTag] = useState<string>('');



  // Set the target tags to the original comment's tags
  useEffect(() => {
    const targetTags: TagType[] = tags.filter((tag) => tag.likelihood !== 'Extremely Unlikely' && tag.likelihood !== 'Unlikely');
    // By the tag's likelihood, add deselected property
    targetTags.forEach((tag) => {
      if (tag.likelihood === 'Extremely Unlikely' || tag.likelihood === 'Unlikely') {
        tag.deselected = true;
      } else {
        tag.deselected = false;
      }
    });
    setTargetTags(targetTags);
  }, [tags]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    previousDraftRef.current = comment;
    setComment(e.target.value);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleInfoClick = () => {
    setShowInfoPopup(true);
    logEvent('open_info_screen', '', window.location.href);
  };

  const handleCloseInfoPopup = () => {
    setShowInfoPopup(false);
  };


  const onTagHover = (tag: TagType) => {
    const eventName = 'onTagHover';
    const payload = {
      comment_id: id,
      tag: tag.frame,
    }
    logEvent(eventName, JSON.stringify(payload), window.location.href);
    setHighlightedTag(tag.frame);
  }

  const onTagHoverOut = () => {
    const eventName = 'onTagHoverOut';
    const payload = {
      comment_id: id,
      tag: highlightedTag
    }
    logEvent(eventName, JSON.stringify(payload), window.location.href);
    setHighlightedTag('');
  }

  const wordCount = comment.trim() ? comment.trim().split(/\s+/).length : 0;

  const getWordCountClass = (count: number) => {
    // if (count < 75 || count > 275) return 'word-count-red';
    // if ((count >= 75 && count < 125) || (count > 225 && count <= 275)) return 'word-count-yellow';
    // if (count >= 125 && count <= 225) return 'word-count-green';
    if (count < 100) return 'word-count-red';
    // if ((count >= 75 && count < 125) || (count > 225 && count <= 275)) return 'word-count-yellow';
    if (count >= 100) return 'word-count-green';
    return '';
  };

  const toggleWordCountInfo = () => {
    setShowWordCountInfo(!showWordCountInfo);
    logEvent('toggle_word_count_info', String(!showWordCountInfo), window.location.href);
  };

  const handleSuggestMoralArguments = useCallback(async () => {
    setIsLoading(true);
    setNewApiCallMade(false);
    try {
      const finalTags = targetTags.filter((tag) => tag.likelihood !== 'Extremely Unlikely' && tag.likelihood !== 'Unlikely' && !tag.deselected);
      console.log(finalTags)
      const newSuggestions = await suggestMoralArguments(originalComment, finalTags, comment);
      previousApiResponseRef.current = { ...lastApiCallResult };
      setLastApiCallResult({ suggestions: newSuggestions });
      setNewApiCallMade(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [originalComment, comment, lastApiCallResult, targetTags]);

  const handleIdentifyLogicalFlaws = useCallback(async () => {
    setIsLoading(true);
    setNewApiCallMade(false);
    try {
      const finalTags = targetTags.filter((tag) => tag.likelihood !== 'Extremely Unlikely' && tag.likelihood !== 'Unlikely' && !tag.deselected);
      console.log(finalTags)
      const newLogicalFlaws = await identifyLogicalFlaws(comment, finalTags, originalComment);
      previousApiResponseRef.current = { ...lastApiCallResult };
      setLastApiCallResult({ logicalFlaws: newLogicalFlaws });
      setNewApiCallMade(true);
    } catch (error) {
      console.error('Error fetching logical flaws:', error);
    } finally {
      setIsLoading(false);
    }
  }, [comment, lastApiCallResult, targetTags]);

  // const handleValidateFrame = useCallback(async () => {
  //   setIsLoading(true);
  //   setNewApiCallMade(false);
  //   try {
  //     const result = await validateFrame(comment, tags.join(', '));
  //     previousApiResponseRef.current = { ...lastApiCallResult };
  //     setLastApiCallResult({ frameValidation: result });
  //     setFrameValidationResult(result);
  //     setNewApiCallMade(true);
  //   } catch (error) {
  //     console.error('Error validating frame:', error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }, [comment, tags, lastApiCallResult]);

  const toggleHighlight = () => {
    const eventName = isHighlightEnabled ? 'disable_highlight' : 'enable_highlight';
    setIsHighlightEnabled(!isHighlightEnabled);
    const payload = {
      comment_id: id,
    }
    logEvent(eventName, JSON.stringify(payload), window.location.href);
  }

  return (
    <div className="reply-screen">
      <div className="reply-header">
        <button className="back-button" onClick={handleBack}>
          <span className="back-arrow">←</span>
        </button>
        <button className="info-button" onClick={handleInfoClick}>
          <img src={questionMarkIcon} alt="Info" className="info-icon" />
        </button>
      </div>
      <h2 className="reply-title">Your comment</h2>
      {originalComment && (
        <div className="original-comment-container">
          <div className="original-comment-header">
            <h3 className="original-comment-title">Original Comment:</h3>
            <div className="tags-container">
              <TagList tags={tags} onTagHover={onTagHover} onTagHoverOut={onTagHoverOut}/>
            </div>
            <div className="comment-controls">
              <HighlightToggle isEnabled={isHighlightEnabled} onToggle={toggleHighlight} />
            </div>
          </div>
          <CommentContent
            content={originalComment}
            tagMatch={transformedTagMatch || []}
            isHighlightEnabled={isHighlightEnabled}
            className={"original-comment-content"}
            highlightedFrame={highlightedTag}
            />
          {/* <div className="original-comment-content">
            <p>{originalComment.split('\n').map((line, index) => (
              <span key={index}>{line}<br /></span> // Preserve line breaks
            ))}</p>
          </div> */}
        </div>
      )}
      <div className="reply--wrapper">
        <div className="reply--title">
          <div>Target Moral Foundation:</div> 
          <div className="tags-container">
            <TagEditor tags={targetTags} setTags={setTargetTags} />
          </div>
        </div>
        <div className="reply-content-wrapper">
          <div className="reply-content">
            <textarea
              value={comment}
              onChange={handleCommentChange}
              placeholder="Type your reply here..."
              className="reply-textarea"
              rows={5} // Added to specify the number of visible rows
            />
            <div className="word-count-container">
              <MoralAlignment text={comment} targetFrame={targetTags} />
              <div className="word-count-wrapper">
                <div className={`word-count ${getWordCountClass(wordCount)}`}>
                  Word count: {wordCount}
                </div>
                {/* <button className="word-count-info-button" onClick={toggleWordCountInfo}>i</button> */}
              </div>
            </div>
          </div>
          <div className="separator"></div>
          <ChatGPTInput
            lastApiCallResult={lastApiCallResult}
            newApiCallMade={newApiCallMade}
            isLoading={isLoading}
          />
        </div>
      </div>
      {showWordCountInfo && (
        <div className="word-count-info-overlay">
          <WordCountInfo isOpen={showWordCountInfo} onClose={toggleWordCountInfo} />
        </div>
      )}
      {showInfoPopup && (
        <div className="info-popup">
          <InfoScreen onClose={handleCloseInfoPopup} />
        </div>
      )}
      <div className="reply-buttons">
        <button className="action-button half-width" onClick={handleSuggestMoralArguments}>Suggest Discussion Points</button>
        <button className="action-button half-width" onClick={handleIdentifyLogicalFlaws}>Feedback on Comment</button>
        
      </div>
      <p className="send-disabled-note">Posting comments is disabled in this demo.</p>
      <button className="send-button" disabled title="Posting comments is disabled in this demo">Send</button>
    </div>
  );
};

export default ReplyScreen;